import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ActiveProductPlanSelectionsMap,
  BenefitProductItem,
  BenefitProductPlanItem,
  PayrollBenefitsAssignmentService,
} from '../../../services/payroll-benefits-assignment/payroll-benefits-assignment.service';
import { CompanyProductSettingMode } from '../companyproductsdialog/companyproductsdialog.component';

export interface CompanyProductPlanLevelsDialogResult {
  mode: CompanyProductSettingMode;
  productPlanIdsMap: Record<string, string[]>;
  productPlanLabelsMap: Record<string, string[]>;
}

@Component({
  selector: 'app-company-product-plan-levels-dialog',
  templateUrl: './company-product-plan-levels-dialog.component.html',
  styleUrls: ['./company-product-plan-levels-dialog.component.scss']
})
export class CompanyProductPlanLevelsDialogComponent {
  public mode: CompanyProductSettingMode;
  public validationMessage = '';
  public expandedProductIds = new Set<string>();

  private effectiveSelectedPlanSelectionsMap: Record<string, string[]> = {};
  private customSelectedPlanSelectionsMap: Record<string, string[]> = {};
  private unrestrictedSelectedPlanSelectionsMap: Record<string, string[]> = {};

  constructor(
    private payrollBenefitsAssignmentService: PayrollBenefitsAssignmentService,
    public dialogRef: MatDialogRef<CompanyProductPlanLevelsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CompanyProductPlanLevelsDialogModel,
  ) {
    this.mode = data?.initialMode || 'custom';
    this.unrestrictedSelectedPlanSelectionsMap = this.buildUnrestrictedSelectionMap(this.products);
    this.effectiveSelectedPlanSelectionsMap = this.normalizePlanSelectionsMap(
      data?.selectedPlanSelectionsMap || {},
      this.unrestrictedSelectedPlanSelectionsMap,
    );
    this.customSelectedPlanSelectionsMap = this.clonePlanSelectionsMap(
      this.mode === 'none'
        ? this.unrestrictedSelectedPlanSelectionsMap
        : this.effectiveSelectedPlanSelectionsMap
    );

    const expandedProductIds = this.products
      .filter((product) => this.isBenefitProductPartiallySelected(product.id, this.getSelectedPlanSelectionsForMode()))
      .map((product) => product.id);
    this.expandedProductIds = new Set(expandedProductIds);
  }

  get products(): BenefitProductItem[] {
    return Array.isArray(this.data?.products) ? this.data.products : [];
  }

  canInherit(): boolean {
    return !!this.data?.canInherit;
  }

  isCustomMode(): boolean {
    return this.mode === 'custom';
  }

  setMode(mode: CompanyProductSettingMode): void {
    if (mode === this.mode) {
      return;
    }

    if (mode === 'custom' && this.mode !== 'custom') {
      this.customSelectedPlanSelectionsMap = this.clonePlanSelectionsMap(this.getSelectedPlanSelectionsForMode());
    }

    this.validationMessage = '';
    this.mode = mode;
  }

  isBenefitProductSelected(productId: string): boolean {
    const product = this.getBenefitProductById(productId);
    if (!product?.plans.length) {
      return false;
    }
    return product.plans.every((plan) => this.isBenefitPlanSelected(productId, plan.id));
  }

  isBenefitProductIndeterminate(productId: string): boolean {
    return this.isBenefitProductPartiallySelected(productId, this.getSelectedPlanSelectionsForMode());
  }

  onBenefitProductToggle(productId: string, checked: boolean): void {
    if (!this.isCustomMode()) {
      return;
    }

    const product = this.getBenefitProductById(productId);
    if (!product?.plans.length) {
      return;
    }

    this.setCustomSelectedPlanSelections(
      productId,
      checked ? this.getBenefitProductAvailablePlanSelections(product) : []
    );
  }

  isBenefitProductExpanded(productId: string): boolean {
    return this.expandedProductIds.has(productId);
  }

  toggleBenefitProductExpansion(productId: string): void {
    const product = this.getBenefitProductById(productId);
    if (!product?.plans.length) {
      return;
    }

    const nextExpanded = new Set(this.expandedProductIds);
    if (nextExpanded.has(productId)) {
      nextExpanded.delete(productId);
    } else {
      nextExpanded.add(productId);
    }
    this.expandedProductIds = nextExpanded;
  }

  isBenefitPlanSelected(productId: string, planId: string): boolean {
    const plan = this.getBenefitProductPlanById(productId, planId);
    if (!plan) {
      return false;
    }

    const selectedPlanSelections = new Set(this.getSelectedPlanSelectionsForProduct(productId));
    return this.getBenefitPlanSelectionValues(plan)
      .every((selectionValue) => selectedPlanSelections.has(selectionValue));
  }

  isBenefitPlanIndeterminate(productId: string, planId: string): boolean {
    const plan = this.getBenefitProductPlanById(productId, planId);
    if (!plan) {
      return false;
    }

    const selectedPlanSelections = new Set(this.getSelectedPlanSelectionsForProduct(productId));
    const planSelectionValues = this.getBenefitPlanSelectionValues(plan);
    const selectedUnderlyingPlanCount = planSelectionValues
      .filter((selectionValue) => selectedPlanSelections.has(selectionValue)).length;
    return selectedUnderlyingPlanCount > 0 && selectedUnderlyingPlanCount < planSelectionValues.length;
  }

  onBenefitPlanToggle(productId: string, planId: string, checked: boolean): void {
    if (!this.isCustomMode()) {
      return;
    }

    const plan = this.getBenefitProductPlanById(productId, planId);
    if (!plan) {
      return;
    }

    const currentPlanSelections = new Set(this.getSelectedPlanSelectionsForProduct(productId));
    const planSelectionValues = this.getBenefitPlanSelectionValues(plan);
    if (checked) {
      planSelectionValues.forEach((selectionValue) => currentPlanSelections.add(selectionValue));
    } else {
      planSelectionValues.forEach((selectionValue) => currentPlanSelections.delete(selectionValue));
    }

    this.setCustomSelectedPlanSelections(productId, Array.from(currentPlanSelections));
  }

  getSelectedBenefitPlanCount(productId: string): number {
    const product = this.getBenefitProductById(productId);
    if (!product?.plans.length) {
      return 0;
    }
    return product.plans.filter((plan) => this.isBenefitPlanSelected(productId, plan.id)).length;
  }

  getProductLabel(product: BenefitProductItem): string {
    return product.vendorName || product.productName || product.id;
  }

  trackProduct(index: number, product: BenefitProductItem): string {
    return product.id;
  }

  trackPlan(index: number, plan: BenefitProductPlanItem): string {
    return plan.id;
  }

  openBenefitPlanDetails(detailsUrl: string, event: Event): void {
    event.stopPropagation();
    const normalizedUrl = `${detailsUrl || ''}`.trim();
    if (!normalizedUrl) {
      return;
    }
    window.open(normalizedUrl, '_blank', 'noopener');
  }

  okClicked(): void {
    if (this.mode !== 'custom') {
      this.dialogRef.close({
        mode: this.mode,
        productPlanIdsMap: {},
        productPlanLabelsMap: {},
      });
      return;
    }

    const invalidProducts = this.products.filter((product) => {
      return this.getSelectedPlanSelectionsForProduct(product.id).length === 0;
    });
    if (invalidProducts.length > 0) {
      invalidProducts.forEach((product) => this.expandedProductIds.add(product.id));
      const firstInvalidProduct = invalidProducts[0];
      this.validationMessage =
        `At least one plan level must remain selected for ${this.getProductLabel(firstInvalidProduct)}. ` +
        'Use Available Products if you want to hide the product entirely.';
      return;
    }

    const normalizedSelections = this.payrollBenefitsAssignmentService.normalizeActiveProductPlanSelectionsMap(
      this.customSelectedPlanSelectionsMap as ActiveProductPlanSelectionsMap
    );
    const planMaps = this.payrollBenefitsAssignmentService.buildCompanyProductPlanRestrictionMaps(
      normalizedSelections,
      this.products,
    );

    this.dialogRef.close({
      mode: this.mode,
      productPlanIdsMap: planMaps.planIdsMap,
      productPlanLabelsMap: planMaps.planLabelsMap,
    });
  }

  onDismiss(): void {
    this.dialogRef.close(undefined);
  }

  private getSelectedPlanSelectionsForMode(): Record<string, string[]> {
    if (this.mode === 'inherit') {
      return this.effectiveSelectedPlanSelectionsMap;
    }

    if (this.mode === 'none') {
      return this.unrestrictedSelectedPlanSelectionsMap;
    }

    return this.customSelectedPlanSelectionsMap;
  }

  private getSelectedPlanSelectionsForProduct(productId: string): string[] {
    return this.getSelectedPlanSelectionsForMode()[productId] || [];
  }

  private setCustomSelectedPlanSelections(productId: string, planSelections: string[]): void {
    const product = this.getBenefitProductById(productId);
    if (!product?.plans.length) {
      return;
    }

    const availablePlanSelections = this.getBenefitProductAvailablePlanSelections(product);
    const normalizedPlanSelections =
      [...new Set((planSelections || []).filter((selectionValue) => availablePlanSelections.includes(selectionValue)))]
        .sort((left, right) => availablePlanSelections.indexOf(left) - availablePlanSelections.indexOf(right));

    this.validationMessage = '';
    this.customSelectedPlanSelectionsMap = {
      ...this.customSelectedPlanSelectionsMap,
      [productId]: normalizedPlanSelections,
    };
  }

  private getBenefitProductById(productId: string): BenefitProductItem | undefined {
    return this.products.find((product) => product.id === productId);
  }

  private getBenefitProductPlanById(productId: string, planId: string): BenefitProductPlanItem | undefined {
    return this.getBenefitProductById(productId)?.plans.find((plan) => plan.id === planId);
  }

  private getBenefitProductAvailablePlanSelections(product: BenefitProductItem): string[] {
    return product.plans.reduce(
      (allSelections: string[], plan) => allSelections.concat(this.getBenefitPlanSelectionValues(plan)),
      [],
    );
  }

  private getBenefitPlanSelectionValues(plan: BenefitProductPlanItem): string[] {
    return plan.selectionMode === 'plan-label' ? [...plan.planLabels] : [...plan.planIds];
  }

  private buildUnrestrictedSelectionMap(products: BenefitProductItem[]): Record<string, string[]> {
    return products.reduce((result: Record<string, string[]>, product) => {
      result[product.id] = this.getBenefitProductAvailablePlanSelections(product);
      return result;
    }, {});
  }

  private normalizePlanSelectionsMap(
    selectedPlanSelectionsMap: ActiveProductPlanSelectionsMap,
    fallbackSelectionsMap: Record<string, string[]>,
  ): Record<string, string[]> {
    const normalizedPlanSelectionsMap =
      this.payrollBenefitsAssignmentService.normalizeActiveProductPlanSelectionsMap(selectedPlanSelectionsMap);

    return this.products.reduce((result: Record<string, string[]>, product) => {
      const availableSelections = this.getBenefitProductAvailablePlanSelections(product);
      const selectedSelections = (normalizedPlanSelectionsMap[product.id] || fallbackSelectionsMap[product.id] || [])
        .filter((selectionValue) => availableSelections.includes(selectionValue));
      result[product.id] = selectedSelections.length > 0
        ? [...selectedSelections]
        : [...availableSelections];
      return result;
    }, {});
  }

  private clonePlanSelectionsMap(planSelectionsMap: Record<string, string[]>): Record<string, string[]> {
    return Object.entries(planSelectionsMap || {}).reduce((result: Record<string, string[]>, [productId, values]) => {
      result[productId] = [...(values || [])];
      return result;
    }, {});
  }

  private isBenefitProductPartiallySelected(
    productId: string,
    selectedPlanSelectionsMap: Record<string, string[]>,
  ): boolean {
    const product = this.getBenefitProductById(productId);
    if (!product?.plans.length) {
      return false;
    }

    const selectedPlanSelections = new Set(selectedPlanSelectionsMap[productId] || []);
    const hasPartialPlanSelection = product.plans.some((plan) => {
      const planSelectionValues = this.getBenefitPlanSelectionValues(plan);
      const selectedUnderlyingPlanCount = planSelectionValues
        .filter((selectionValue) => selectedPlanSelections.has(selectionValue)).length;
      return selectedUnderlyingPlanCount > 0 && selectedUnderlyingPlanCount < planSelectionValues.length;
    });
    const selectedPlanCount = product.plans.filter((plan) => {
      return this.getBenefitPlanSelectionValues(plan)
        .every((selectionValue) => selectedPlanSelections.has(selectionValue));
    }).length;

    return hasPartialPlanSelection || (selectedPlanCount > 0 && selectedPlanCount < product.plans.length);
  }
}

export class CompanyProductPlanLevelsDialogModel {
  constructor(
    public products: BenefitProductItem[] = [],
    public selectedPlanSelectionsMap: ActiveProductPlanSelectionsMap = {},
    public companyName: string = '',
    public canInherit: boolean = false,
    public parentCompanyName: string = '',
    public initialMode: CompanyProductSettingMode = 'custom',
  ) {}
}
